import { triggerEvent } from './util/event-handler';

// ============================================================================
// Antumo Toast
// ============================================================================
//
// Imperative Toast manager that renders DS `.alert` markup into a
// fixed-position `.antumo-toast-container` (one per screen position). Unlike
// Modal/Sidebar/Tooltip, Toast is NOT attached to a host element — it is a
// static factory that creates DOM on demand:
//
//     Toast.show({ type: 'success', title: 'Saved', message: 'Done.' });
//     Toast.show({ type: 'error', message: 'Network error', duration: 0 });
//     Toast.dismissAll();
//
// CSS contract: lives in components/base/_notifications.scss
//   - `.alert.alert-{success|error|warning|info}` — colored icon variants
//   - `.antumo-toast-container.antumo-toast-container--{position}` — anchor
//   - `.alert.antumo-toast-entering` / `.antumo-toast-leaving` — animation
//
// Events (bubble from each .alert element):
//   antumo:toast:show     (cancelable, fires before insertion)
//   antumo:toast:shown    (after insertion + entering class removed)
//   antumo:toast:hide     (cancelable, fires before exit animation)
//   antumo:toast:hidden   (after removal)
//
// Icons are inlined Lucide SVGs (DS Golden Rule: no icon fonts).
// ============================================================================

const EVENT_SHOW = 'antumo:toast:show';
const EVENT_SHOWN = 'antumo:toast:shown';
const EVENT_HIDE = 'antumo:toast:hide';
const EVENT_HIDDEN = 'antumo:toast:hidden';

const CONTAINER_ID = 'antumo-toast-container';
const CONTAINER_BASE_CLASS = 'antumo-toast-container';
const CLASS_ENTERING = 'antumo-toast-entering';
const CLASS_LEAVING = 'antumo-toast-leaving';

const ANIMATION_DURATION = 150;

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export type ToastPosition =
  | 'top-right'
  | 'top-left'
  | 'bottom-right'
  | 'bottom-left';

export interface ToastLink {
  /** Visible CTA label. */
  text: string;
  /** Click handler. Receives the toast instance so handlers can dismiss. */
  onClick?: (toast: Toast) => void;
  /** Optional href; when set the link renders as <a>, otherwise <button>. */
  href?: string;
  /** When false, the toast stays open after the link is clicked. Default true (Blazor bridge only). */
  dismissOnClick?: boolean;
}

export interface ToastOptions {
  /** Variant: drives icon + color. Default 'info'. */
  type?: ToastType;
  /** Bold heading (optional). When omitted, message becomes the title. */
  title?: string;
  /** Body copy. Required. */
  message: string;
  /**
   * Auto-dismiss delay in ms. `0` = sticky (user must close manually).
   * Default 5000ms.
   */
  duration?: number;
  /** Where to anchor the container. Default 'top-right'. */
  position?: ToastPosition;
  /** Show the close (X) button. Default true. */
  closable?: boolean;
  /** Optional CTA link rendered under the message. */
  link?: ToastLink;
}

const DEFAULT_OPTIONS: Required<Omit<ToastOptions, 'title' | 'link'>> = {
  type: 'info',
  message: '',
  duration: 5000,
  position: 'top-right',
  closable: true,
};

// ---------------------------------------------------------------------------
// Lucide SVG icons (inline, no external font/sprite)
// Sources: lucide.dev — check-circle, alert-circle, alert-triangle, info, x
// ---------------------------------------------------------------------------

const SVG_ATTRS =
  'width="20" height="20" viewBox="0 0 24 24" fill="none" ' +
  'stroke="currentColor" stroke-width="1.5" stroke-linecap="round" ' +
  'stroke-linejoin="round" aria-hidden="true"';

const ICONS: Record<ToastType, string> = {
  success:
    `<svg ${SVG_ATTRS}>` +
    '<circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/>' +
    '</svg>',
  error:
    `<svg ${SVG_ATTRS}>` +
    '<circle cx="12" cy="12" r="10"/>' +
    '<line x1="12" y1="8" x2="12" y2="12"/>' +
    '<line x1="12" y1="16" x2="12.01" y2="16"/>' +
    '</svg>',
  warning:
    `<svg ${SVG_ATTRS}>` +
    '<path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"/>' +
    '<line x1="12" y1="9" x2="12" y2="13"/>' +
    '<line x1="12" y1="17" x2="12.01" y2="17"/>' +
    '</svg>',
  info:
    `<svg ${SVG_ATTRS}>` +
    '<circle cx="12" cy="12" r="10"/>' +
    '<line x1="12" y1="16" x2="12" y2="12"/>' +
    '<line x1="12" y1="8" x2="12.01" y2="8"/>' +
    '</svg>',
};

const CLOSE_ICON =
  `<svg ${SVG_ATTRS}>` +
  '<path d="M18 6 6 18"/><path d="m6 6 12 12"/>' +
  '</svg>';

// ---------------------------------------------------------------------------
// ARIA mapping
// success/info → polite; error/warning → assertive
// ---------------------------------------------------------------------------

function ariaForType(type: ToastType): { role: string; live: string } {
  if (type === 'error' || type === 'warning') {
    return { role: 'alert', live: 'assertive' };
  }
  return { role: 'status', live: 'polite' };
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// ---------------------------------------------------------------------------
// Container registry — one container per position
// ---------------------------------------------------------------------------

const containers = new Map<ToastPosition, HTMLElement>();

function getContainer(position: ToastPosition): HTMLElement {
  const existing = containers.get(position);
  if (existing && existing.isConnected) return existing;

  const el = document.createElement('div');
  el.id = `${CONTAINER_ID}-${position}`;
  el.className = `${CONTAINER_BASE_CLASS} ${CONTAINER_BASE_CLASS}--${position}`;
  el.setAttribute('aria-live', 'polite');
  el.setAttribute('aria-relevant', 'additions');
  document.body.appendChild(el);
  containers.set(position, el);
  return el;
}

// ---------------------------------------------------------------------------
// Toast class
// ---------------------------------------------------------------------------

export class Toast {
  static DefaultOptions: Required<Omit<ToastOptions, 'title' | 'link'>> =
    DEFAULT_OPTIONS;

  /** Currently visible toasts, in insertion order. */
  private static _instances: Toast[] = [];

  private _element: HTMLElement | null = null;
  private _options: Required<Omit<ToastOptions, 'title' | 'link'>> & {
    title?: string;
    link?: ToastLink;
  };
  private _autoDismissTimer: ReturnType<typeof setTimeout> | null = null;
  private _remainingMs: number;
  private _timerStartedAt: number = 0;
  private _isHiding = false;
  private _boundOnMouseEnter: () => void;
  private _boundOnMouseLeave: () => void;
  private _boundOnCloseClick: (e: Event) => void;
  private _boundOnLinkClick: ((e: Event) => void) | null = null;

  private constructor(options: ToastOptions) {
    if (!options || typeof options.message !== 'string' || options.message === '') {
      throw new TypeError('Toast: `message` is required');
    }
    this._options = { ...DEFAULT_OPTIONS, ...options };
    this._remainingMs = this._options.duration;
    this._boundOnMouseEnter = this._pauseTimer.bind(this);
    this._boundOnMouseLeave = this._resumeTimer.bind(this);
    this._boundOnCloseClick = this._handleCloseClick.bind(this);
  }

  /**
   * Show a toast. Returns the Toast instance so callers can dismiss
   * programmatically (e.g. when a long-running operation finishes).
   */
  static show(options: ToastOptions): Toast {
    const toast = new Toast(options);
    toast._render();
    return toast;
  }

  /** Convenience helpers — match abp.notify.* signatures. */
  static success(message: string, title?: string, opts: Partial<ToastOptions> = {}): Toast {
    return Toast.show({ ...opts, type: 'success', title, message });
  }
  static error(message: string, title?: string, opts: Partial<ToastOptions> = {}): Toast {
    return Toast.show({ ...opts, type: 'error', title, message });
  }
  static warning(message: string, title?: string, opts: Partial<ToastOptions> = {}): Toast {
    return Toast.show({ ...opts, type: 'warning', title, message });
  }
  static info(message: string, title?: string, opts: Partial<ToastOptions> = {}): Toast {
    return Toast.show({ ...opts, type: 'info', title, message });
  }

  /** Dismiss every currently visible toast. */
  static dismissAll(): void {
    [...Toast._instances].forEach((t) => t.hide());
  }

  // Blazor bridge: C# passes a DotNetObjectReference instead of a JS onClick.
  // A link click (when it has no href) becomes invokeMethodAsync('OnLinkClick');
  // toast removal becomes invokeMethodAsync('OnHidden') so C# can dispose the
  // reference. Mirrors Dropdown.initWithDotNetRef used by AntumoSelect.
  static showFromDotNet(
    options: ToastOptions,
    dotNetRef: {
      invokeMethodAsync: (method: string, ...args: unknown[]) => Promise<unknown>;
    } | null,
  ): Toast {
    const opts: ToastOptions = { ...options };

    if (opts.link && dotNetRef && !opts.link.href) {
      const link = opts.link;
      opts.link = {
        ...link,
        onClick: (toast: Toast): void => {
          void dotNetRef.invokeMethodAsync('OnLinkClick');
          if (link.dismissOnClick !== false) toast.hide();
        },
      };
    }

    const toast = Toast.show(opts);

    if (dotNetRef && toast.element) {
      toast.element.addEventListener(
        EVENT_HIDDEN,
        () => {
          void dotNetRef.invokeMethodAsync('OnHidden');
        },
        { once: true },
      );
    }

    return toast;
  }

  get element(): HTMLElement | null {
    return this._element;
  }

  hide(): void {
    if (this._isHiding || !this._element) return;

    const hideEvent = triggerEvent(this._element, EVENT_HIDE, {}, true);
    if (hideEvent.defaultPrevented) return;

    this._isHiding = true;
    this._clearAutoDismiss();

    const el = this._element;
    el.classList.add(CLASS_LEAVING);

    const finish = (): void => {
      this._removeFromDOM();
    };

    if (this._isReducedMotion()) {
      finish();
    } else {
      setTimeout(finish, ANIMATION_DURATION);
    }
  }

  /** Manually dispose — alias to hide() for API symmetry. */
  dispose(): void {
    this.hide();
  }

  // ── Internal: rendering ────────────────────────────────────────────────

  private _render(): void {
    const container = getContainer(this._options.position);

    const el = this._buildElement();

    // Insert before dispatching so antumo:toast:show bubbles to document.
    container.appendChild(el);

    const showEvent = triggerEvent(el, EVENT_SHOW, { toast: this }, true);
    if (showEvent.defaultPrevented) {
      el.remove();
      this._element = null;
      return;
    }

    this._element = el;
    Toast._instances.push(this);

    // Animate IN: add entering class first, then remove on next frame so
    // the browser commits the initial state before transitioning.
    el.classList.add(CLASS_ENTERING);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.classList.remove(CLASS_ENTERING);
      });
    });

    this._attachListeners(el);
    this._startAutoDismiss();

    triggerEvent(el, EVENT_SHOWN, { toast: this }, false);
  }

  private _buildElement(): HTMLElement {
    const { type, title, message, closable, link } = this._options;
    const aria = ariaForType(type);

    const root = document.createElement('div');
    root.className = `alert alert-${type}`;
    root.setAttribute('role', aria.role);
    root.setAttribute('aria-live', aria.live);
    root.setAttribute('aria-atomic', 'true');

    // Build innerHTML — escape user-provided strings; SVG icon is trusted.
    const titleHtml = title
      ? `<p class="alert-title">${escapeHtml(title)}</p>`
      : '';
    const messageHtml = `<p class="alert-message">${escapeHtml(message)}</p>`;
    const linkHtml = link
      ? this._buildLinkHtml(link)
      : '';
    const closeHtml = closable
      ? `<button type="button" class="alert-close-button" aria-label="Close">${CLOSE_ICON}</button>`
      : '';

    root.innerHTML =
      '<div class="alert-content">' +
      `<span class="alert-icon">${ICONS[type]}</span>` +
      '<div class="alert-body">' +
      '<div class="alert-header">' +
      titleHtml +
      messageHtml +
      '</div>' +
      linkHtml +
      '</div>' +
      '</div>' +
      closeHtml;

    return root;
  }

  private _buildLinkHtml(link: ToastLink): string {
    const text = escapeHtml(link.text);
    if (link.href) {
      const href = escapeHtml(link.href);
      return `<a class="alert-link" href="${href}">${text}</a>`;
    }
    return `<button type="button" class="alert-link">${text}</button>`;
  }

  // ── Internal: listeners & timer ────────────────────────────────────────

  private _attachListeners(el: HTMLElement): void {
    const closeBtn = el.querySelector('.alert-close-button');
    if (closeBtn) {
      closeBtn.addEventListener('click', this._boundOnCloseClick);
    }

    const linkEl = el.querySelector('.alert-link');
    if (linkEl && this._options.link?.onClick) {
      this._boundOnLinkClick = (e: Event): void => {
        if (!this._options.link?.href) e.preventDefault();
        this._options.link?.onClick?.(this);
      };
      linkEl.addEventListener('click', this._boundOnLinkClick);
    }

    // Hover pauses auto-dismiss so users can read long messages.
    if (this._options.duration > 0) {
      el.addEventListener('mouseenter', this._boundOnMouseEnter);
      el.addEventListener('mouseleave', this._boundOnMouseLeave);
      // Keyboard focus also pauses (a11y).
      el.addEventListener('focusin', this._boundOnMouseEnter);
      el.addEventListener('focusout', this._boundOnMouseLeave);
    }
  }

  private _handleCloseClick(e: Event): void {
    e.preventDefault();
    this.hide();
  }

  private _startAutoDismiss(): void {
    if (this._options.duration <= 0) return;
    this._timerStartedAt = Date.now();
    this._autoDismissTimer = setTimeout(() => this.hide(), this._remainingMs);
  }

  private _pauseTimer(): void {
    if (this._autoDismissTimer === null) return;
    clearTimeout(this._autoDismissTimer);
    this._autoDismissTimer = null;
    const elapsed = Date.now() - this._timerStartedAt;
    this._remainingMs = Math.max(0, this._remainingMs - elapsed);
  }

  private _resumeTimer(): void {
    if (this._options.duration <= 0 || this._autoDismissTimer !== null) return;
    if (this._remainingMs <= 0) {
      this.hide();
      return;
    }
    this._timerStartedAt = Date.now();
    this._autoDismissTimer = setTimeout(() => this.hide(), this._remainingMs);
  }

  private _clearAutoDismiss(): void {
    if (this._autoDismissTimer !== null) {
      clearTimeout(this._autoDismissTimer);
      this._autoDismissTimer = null;
    }
  }

  // ── Internal: removal ──────────────────────────────────────────────────

  private _removeFromDOM(): void {
    const el = this._element;
    if (!el) return;

    el.removeEventListener('mouseenter', this._boundOnMouseEnter);
    el.removeEventListener('mouseleave', this._boundOnMouseLeave);
    el.removeEventListener('focusin', this._boundOnMouseEnter);
    el.removeEventListener('focusout', this._boundOnMouseLeave);
    const closeBtn = el.querySelector('.alert-close-button');
    if (closeBtn) closeBtn.removeEventListener('click', this._boundOnCloseClick);
    const linkEl = el.querySelector('.alert-link');
    if (linkEl && this._boundOnLinkClick) {
      linkEl.removeEventListener('click', this._boundOnLinkClick);
    }

    const idx = Toast._instances.indexOf(this);
    if (idx >= 0) Toast._instances.splice(idx, 1);

    triggerEvent(el, EVENT_HIDDEN, { toast: this }, false);
    el.remove();
    this._element = null;
  }

  private _isReducedMotion(): boolean {
    return (
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }
}
